import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Plus,
  Clock,
  AlertTriangle,
  Star,
  Check,
  ChevronLeft,
  ChevronRight,
  SlidersHorizontal,
  CalendarDays,
  Calendar,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { MenuItem, Category, Filters } from "../types";
import { getMenu } from "../api/menu";
import { getCategories } from "../api/category";
import { toast } from "sonner";
import { AdvancedFilters } from "./AdvancedFilters";
import {
  formatDateByTimezone,
  getTodayFormatted,
  isTokenExpired,
  MAX_VISIBILITY_DAYS,
  WS_DISH_AVAILABILITIES_TOPIC,
  WS_ORDER_TOPIC,
  WS_SERVER_URL,
} from "../utils/utils";
import { ApiResponse, refreshAccessToken } from "../api/auth";
import { getItem } from "../utils/localStorage";
import { useWebSocket } from "../api/useWebSocket";

interface MenuProps {
  onAddToCart: (item: Omit<MenuItem, "quantity">) => void;
}

const Menu: React.FC<MenuProps> = ({ onAddToCart }) => {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<Filters>({
    category: "all",
    minPrice: "",
    maxPrice: "",
    searchQuery: "",
    showFutureDishes: false,
    availabilityDate: "",
  });
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [addingToCart, setAddingToCart] = useState<string | null>(null);
  const [addedToCart, setAddedToCart] = useState<string | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const token = getItem("authToken") as string | undefined;
  const [accessToken, setAccessToken] = useState<string | undefined>(token);

  const handleGetToken = useCallback(async () => {
    if (!accessToken) return undefined;

    if (isTokenExpired(accessToken)) {
      try {
        const freshToken = await refreshAccessToken();
        setAccessToken(freshToken.data?.accessToken);
        return freshToken.data?.accessToken;
      } catch (error: any) {
        toast.error(error.error.message);
        setAccessToken(undefined);
        return undefined;
      }
    }
    // Si le token est toujours valide
    return accessToken;
  }, [accessToken]);

  // Vous n'avez pas besoin de mettre menuItems en dépendance ici !
  const handleMenuUpdate = useCallback(
    (updatedMenuItem: MenuItem) => {
      // setMenuItems reçoit l'état actuel (prevItems) en argument
      setMenuItems((prevItems) => {
        let isUpdated = false;

        // Cloner et mapper en une seule fois
        const newItems = prevItems.map((item) => {
          if (item.id === updatedMenuItem.id) {
            isUpdated = true;
            return updatedMenuItem; // Retourne l'élément mis à jour
          }
          return item; // Retourne l'élément original
        });

        // Si aucun élément n'a été mis à jour, on l'ajoute
        console.log(
          "filters.availabilityDate",
          filters.availabilityDate,
          updatedMenuItem.servingDate,
        );
        if (
          !isUpdated &&
          (updatedMenuItem.servingDate === getTodayFormatted() ||
            updatedMenuItem.servingDate === filters.availabilityDate)
        ) {
          return [...newItems, updatedMenuItem];
        }

        return newItems;
      });

      console.log("MenuItem update received:", updatedMenuItem);
    },
    [filters.availabilityDate],
  ); // <-- Le tableau de dépendances est vide, pas de problème de stale closure!

  useWebSocket<MenuItem>({
    serverUrl: WS_SERVER_URL,
    topic: WS_DISH_AVAILABILITIES_TOPIC,
    onMessage: handleMenuUpdate,
    getToken: handleGetToken,
    debug: true,
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const fetchedCategories = await getCategories();
        setCategories(fetchedCategories.data!);
      } catch (error: any) {
        toast.error(error.error.message);
      }
    };

    fetchCategories();
  }, []);

  // Logique de récupération de menu mise à jour
  const fetchMenu = useCallback(
    async (resetPage: boolean = false) => {
      console.log("currentPage : ", currentPage);
      setLoading(true);
      const pageToFetch = resetPage ? 0 : currentPage;
      console.log("pageToFetch : ", pageToFetch);

      try {
        const categoriesToFetch =
          filters.category === "all" ? undefined : [filters.category];

        // Date: today si on ne veut pas les plats programmés, sinon undefined (pour tout récupérer)
        const dateToFetch = filters.availabilityDate
          ? filters.availabilityDate
          : undefined;

        const page = await getMenu(
          pageToFetch,
          10,
          categoriesToFetch,
          undefined,
          undefined,
          dateToFetch,
        );

        // --- FILTRAGE CÔTÉ CLIENT pour les critères non gérés par l'API (Prix, Disponibilité, Nom, etc.) ---
        let filteredItems = page.data!.content;

        // 2. Filtrage par prix
        if (filters.minPrice !== "") {
          filteredItems = filteredItems.filter(
            (item) =>
              item.dish.price >= parseFloat(filters.minPrice.toString()),
          );
        }
        if (filters.maxPrice !== "") {
          filteredItems = filteredItems.filter(
            (item) =>
              item.dish.price <= parseFloat(filters.maxPrice.toString()),
          );
        }

        // 3. Filtrage par nom/recherche
        if (filters.searchQuery.trim() !== "") {
          const query = filters.searchQuery.toLowerCase();
          filteredItems = filteredItems.filter(
            (item) =>
              item.dish.name.toLowerCase().includes(query) ||
              item.dish.description.toLowerCase().includes(query),
          );
        }

        setMenuItems((prev) => {
          if (pageToFetch === 0) {
            return filteredItems.sort((a, b) => {
              // 1. Tri par Date (Priorité #1 si le filtre "Future Dishes" est actif)
              // Cela garantit que si on a des plats futurs, l'ordre est chronologique
              if (filters.availabilityDate) {
                // Supposons que servingDate est la date de service
                const dateA = new Date(a.servingDate).getTime();
                const dateB = new Date(b.servingDate).getTime();

                // Si les dates sont différentes, trier par date (croissant: plus proche d'abord)
                if (dateA !== dateB) {
                  return dateA - dateB;
                }
                // Si les dates sont égales, on passe au critère suivant (disponibilité)
              }

              // 2. Tri par Disponibilité (Priorité #2, ou #1 si showFutureDishes est false)
              // Les plats disponibles viennent avant les autres.
              const isA_Available =
                a.availabilityState === "AVAILABLE" ||
                a.availabilityState === "SOON_FINISHED";
              const isB_Available =
                b.availabilityState === "AVAILABLE" ||
                b.availabilityState === "SOON_FINISHED";

              if (isA_Available && !isB_Available) {
                return -1; // a vient avant b
              }
              if (!isA_Available && isB_Available) {
                return 1; // b vient avant a
              }

              // 3. Tri par Nom (Critère secondaire)
              // S'applique lorsque les disponibilités/dates sont les mêmes.
              return a.dish.name.localeCompare(b.dish.name);
            });
          } else {
            const newfilteredItems = [...prev, ...filteredItems];
            return newfilteredItems.sort((a, b) => {
              // 1. Tri par Date (Priorité #1 si le filtre "Future Dishes" est actif)
              // Cela garantit que si on a des plats futurs, l'ordre est chronologique
              if (filters.availabilityDate) {
                // Supposons que servingDate est la date de service
                const dateA = new Date(a.servingDate).getTime();
                const dateB = new Date(b.servingDate).getTime();

                // Si les dates sont différentes, trier par date (croissant: plus proche d'abord)
                if (dateA !== dateB) {
                  return dateA - dateB;
                }
                // Si les dates sont égales, on passe au critère suivant (disponibilité)
              }

              // 2. Tri par Disponibilité (Priorité #2, ou #1 si showFutureDishes est false)
              // Les plats disponibles viennent avant les autres.
              const isA_Available =
                a.availabilityState === "AVAILABLE" ||
                a.availabilityState === "SOON_FINISHED";
              const isB_Available =
                b.availabilityState === "AVAILABLE" ||
                b.availabilityState === "SOON_FINISHED";

              if (isA_Available && !isB_Available) {
                return -1; // a vient avant b
              }
              if (!isA_Available && isB_Available) {
                return 1; // b vient avant a
              }

              // 3. Tri par Nom (Critère secondaire)
              // S'applique lorsque les disponibilités/dates sont les mêmes.
              return a.dish.name.localeCompare(b.dish.name);
            });
          }
        });
        setTotalPages(page.data!.totalPages);
      } catch (error: any) {
        toast.error(error.error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters, currentPage],
  ); // Dépendances importantes

  useEffect(() => {
    // Récupère les plats lors du changement de page
    if (currentPage > 0) {
      fetchMenu(false);
    }
  }, [currentPage, fetchMenu]);

  useEffect(() => {
    // Réinitialise la page et recharge si les filtres changent
    if (currentPage === 0) {
      fetchMenu(true);
    } else {
      setCurrentPage(0); // Réinitialise currentPage pour déclencher le useEffect précédent
    }
  }, [filters, i18n.language]);

  // Fonction de gestion des changements de filtre
  const handleFilterChange = (
    key: keyof Filters,
    value: Filters[keyof Filters],
  ) => {
    if (key === "showFutureDishes") {
      setCurrentPage(0);
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const getAvailabilityBadge = (
    availability: MenuItem["availabilityState"],
    stock: string,
  ) => {
    let availabilityStatus: "available" | "soon_finished" | "finished";
    switch (availability) {
      case "AVAILABLE":
        availabilityStatus = "available";
        break;
      case "SOON_FINISHED":
        availabilityStatus = "soon_finished";
        break;
      case "FINISHED":
        availabilityStatus = "finished";
        break;
      default:
        availabilityStatus = "finished";
    }

    switch (availabilityStatus) {
      case "available":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700 border border-green-200">
            {t("menu.available")}
            {" : "}
            {stock}
          </span>
        );

      case "soon_finished":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700 border border-yellow-200">
            {t("menu.soon_finished")}
            {" : "}
            {stock}
          </span>
        );
      case "finished":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 border border-red-200">
            <AlertTriangle className="w-3 h-3 mr-1" strokeWidth={1.5} />
            {t("menu.finished")}
            {" : "}
            {stock}
          </span>
        );
    }
  };

  const handleAddToCart = async (item: Omit<MenuItem, "quantity">) => {
    const user = localStorage.getItem("user");
    if (!user) {
      toast.error(t("menu.login"));
      return;
    }

    setAddingToCart(item.id);

    // Simulation d'un délai pour l'animation d'ajout
    await new Promise((resolve) => setTimeout(resolve, 800));

    onAddToCart(item);

    setAddingToCart(null);
    setAddedToCart(item.id);

    // Réinitialiser l'état après l'animation de confirmation
    setTimeout(() => {
      setAddedToCart(null);
    }, 2000);
  };

  return (
    <div className="container mx-auto px-4 py-12">
      {/* Hero Section */}
      <div className="text-center mb-16">
        <h1 className="text-5xl font-bold text-black mb-6 leading-tight">
          {t("menu.title")}
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          {t("menu.subtitle")}
        </p>
      </div>

      {/* Bouton pour ouvrir les filtres sur mobile */}
      <div className="mb-8 md:hidden">
        <button
          onClick={() => setIsFiltersOpen(true)}
          className="flex items-center space-x-2 px-5 py-3 bg-orange-500 text-white rounded-xl shadow-lg hover:bg-orange-600 transition-colors duration-200 font-medium w-full justify-center"
        >
          <SlidersHorizontal className="w-5 h-5" />
          <span>{t("filters.openFilters")}</span>
        </button>
      </div>

      {/* Contenu principal (Filtres + Grille de menu) */}
      <div className="flex flex-col md:flex-row gap-10">
        {/* Colonne des Filtres */}
        <div className="md:w-1/4">
          <AdvancedFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            categories={categories}
            t={t}
            isOpen={isFiltersOpen}
            onClose={() => setIsFiltersOpen(false)}
          />
        </div>

        {/* Colonne du Menu */}
        <div className="md:w-3/4">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
              <h2 className="text-3xl font-bold text-black mb-4">
                Loading Menu...
              </h2>
              <p className="text-gray-600 text-lg">Please wait a moment.</p>
            </div>
          ) : (
            <>
              {menuItems.length === 0 ? (
                <div className="text-center py-16 bg-gray-50 rounded-xl">
                  <AlertTriangle className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold text-black mb-2">
                    {t("menu.noResultsTitle")}
                  </h2>
                  <p className="text-gray-600">{t("menu.noResultsText")}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
                  {/* ... Mapping des menuItems existant ... */}
                  {menuItems.map((item, index) => (
                    <div
                      key={`${item.id}-${index}`}
                      className={`card overflow-hidden group ${
                        item.availabilityState !== "AVAILABLE"
                          ? "opacity-50 pointer-events-none"
                          : ""
                      }`}
                    >
                      <div className="relative overflow-hidden">
                        <img
                          src={item.dish.imageUrl}
                          alt={item.dish.name}
                          className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        <div className="absolute top-3 right-3">
                          {getAvailabilityBadge(
                            item.availabilityState,
                            (
                              item.quantityPlanned - item.quantitySold
                            ).toString(),
                          )}
                        </div>
                      </div>

                      <div className="p-6">
                        <div className="mb-3">
                          <h3 className="text-xl font-semibold text-black mb-2">
                            {item.dish.name}
                          </h3>

                          {/* Juste après le titre h3 ou les étoiles */}
                          <div className="relative mb-4">
                            <p
                              className="text-gray-600 text-sm leading-relaxed text-justify hyphens-auto line-clamp-2 group-hover:line-clamp-none transition-all duration-300"
                              style={{ hyphens: "auto", WebkitHyphens: "auto" }}
                            >
                              {item.dish.description}
                            </p>
                          </div>

                          {/* NOUVEAU: Section d'information (Servidate et Stock si non affiché sur l'image) */}
                          <div className="flex justify-between items-center mb-4 text-sm">
                            {/* Servidate (Service/Date) */}
                            {item.servingDate && (
                              <div className="flex align-center text-gray-700 bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />{" "}
                                <span>{`${t(
                                  "menu.servedOn",
                                )}: ${formatDateByTimezone(
                                  `${item.servingDate}T12:00:00`,
                                  i18n.language,
                                )}`}</span>
                              </div>
                            )}
                          </div>
                          {/* FIN NOUVEAU */}

                          <div className="flex items-center space-x-1 mb-2">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className="w-4 h-4 text-orange-400 fill-orange-400"
                                strokeWidth={1}
                              />
                            ))}
                            <span className="text-sm text-gray-500 ml-2">
                              (5.0)
                            </span>
                          </div>
                        </div>

                        <div className="flex flex-col items-end space-y-3">
                          <span className="text-2xl font-bold text-black">
                            {item.dish.price.toFixed(2)} CAD
                          </span>
                          {/* Bouton Ajouter au Panier (Pleine largeur) */}
                          <button
                            onClick={() => handleAddToCart(item)}
                            disabled={item.availabilityState === "FINISHED"}
                            className={`w-full flex items-center justify-center space-x-2 px-4 py-1 rounded-lg font-medium transition-all duration-300 transform ${
                              item.availabilityState === "FINISHED"
                                ? "bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200"
                                : addingToCart === item.id
                                  ? "bg-orange-600 text-white scale-95 shadow-lg"
                                  : addedToCart === item.id
                                    ? "bg-green-500 text-white scale-105 shadow-xl"
                                    : "btn-primary hover:scale-105"
                            }`}
                          >
                            {/* ... (Contenu du bouton inchangé) ... */}
                            {addingToCart === item.id ? (
                              <>
                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                <span>{t("menu.adding")}</span>
                              </>
                            ) : addedToCart === item.id ? (
                              <>
                                <Check
                                  className="w-4 h-4 animate-bounce"
                                  strokeWidth={2}
                                />
                                <span>{t("menu.added")}</span>
                              </>
                            ) : (
                              <>
                                <Plus className="w-4 h-4" strokeWidth={1.5} />
                                <span>{t("menu.add")}</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && currentPage < totalPages - 1 && (
                <div className="flex justify-center mt-12">
                  <button
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={loading}
                    className="flex items-center space-x-2 px-6 py-3 bg-orange-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-orange-600 transition-colors duration-200 font-medium"
                  >
                    <span>{t("pagination.loadMore", "Load More")}</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Menu;
